/**
 * Search Term Visibility Settings
 *
 * Allows admins to control which search terms are visible to non-admin users.
 * Non-admin users can only see results from visible search terms.
 */

import { useState, useEffect, useMemo } from 'react';
import { Card, Table, Switch, Button, Space, Typography, Input, Alert } from 'antd';
import { SearchOutlined, SaveOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { useSearchTerms } from '@/hooks/usePlaces';
import { useVisibleSearchTerms, useUpdateVisibleSearchTerms } from '@/hooks/useSettings';

const { Text } = Typography;

export function SearchTermVisibility() {
  const { data: allTerms = [], isLoading: termsLoading } = useSearchTerms();
  const { data: visibleTerms = [], isLoading: visibleLoading } = useVisibleSearchTerms();
  const updateVisible = useUpdateVisibleSearchTerms();

  const [localVisible, setLocalVisible] = useState<Set<string>>(new Set());
  const [dirty, setDirty] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');

  // Sync from server
  useEffect(() => {
    if (visibleTerms) {
      setLocalVisible(new Set(visibleTerms));
      setDirty(false);
    }
  }, [visibleTerms]);

  const handleToggle = (searchQuery: string, checked: boolean) => {
    setLocalVisible((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(searchQuery);
      } else {
        next.delete(searchQuery);
      }
      return next;
    });
    setDirty(true);
  };

  const handleSelectAll = () => {
    setLocalVisible(new Set(allTerms.map((t) => t.searchQuery)));
    setDirty(true);
  };

  const handleDeselectAll = () => {
    setLocalVisible(new Set());
    setDirty(true);
  };

  const handleSave = () => {
    updateVisible.mutate(Array.from(localVisible));
    setDirty(false);
  };

  const filteredTerms = useMemo(() => {
    if (!searchFilter) return allTerms;
    const lower = searchFilter.toLowerCase();
    return allTerms.filter((t) => t.searchQuery.toLowerCase().includes(lower));
  }, [allTerms, searchFilter]);

  const columns = [
    {
      title: 'Visible',
      dataIndex: 'searchQuery',
      key: 'visible',
      width: 80,
      render: (searchQuery: string) => (
        <Switch
          checked={localVisible.has(searchQuery)}
          onChange={(checked) => handleToggle(searchQuery, checked)}
          checkedChildren={<EyeOutlined />}
          unCheckedChildren={<EyeInvisibleOutlined />}
        />
      ),
    },
    {
      title: 'Search Term',
      dataIndex: 'searchQuery',
      key: 'searchQuery',
    },
    {
      title: 'Places',
      dataIndex: 'placeCount',
      key: 'placeCount',
      width: 100,
      render: (count: number) => <Text type="secondary">{count.toLocaleString()}</Text>,
    },
  ];

  const isLoading = termsLoading || visibleLoading;

  return (
    <div>
      <Alert
        message="Control which search terms non-admin users can see"
        description="Only results from visible search terms will be shown to non-admin users. If no terms are selected, non-admin users will see no results."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <Input
              placeholder="Filter terms..."
              prefix={<SearchOutlined />}
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              allowClear
              style={{ width: 250 }}
            />
            <Button size="small" onClick={handleSelectAll}>
              Select All
            </Button>
            <Button size="small" onClick={handleDeselectAll}>
              Deselect All
            </Button>
          </Space>
          <Space>
            <Text type="secondary">
              {localVisible.size} of {allTerms.length} visible
            </Text>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={updateVisible.isPending}
              disabled={!dirty}
            >
              Save
            </Button>
          </Space>
        </Space>
      </Card>

      <Table
        dataSource={filteredTerms}
        columns={columns}
        rowKey="searchQuery"
        loading={isLoading}
        size="small"
        pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
      />
    </div>
  );
}

export default SearchTermVisibility;
